import { Injectable } from '@angular/core';
import { Preferences } from '@capacitor/preferences';
import {
  INITIAL_NEWSFEED_FILTERS,
  NewsfeedFilters
} from '../../declarations/filter.interface';
import {
  ArticleEventFilter,
  INITIAL_EVENT_FILTERS
} from '../../declarations/article.interface';
import { STORAGE_KEYS } from '../../declarations/storage-keys.enum';
import {
  SET_CURRENT_TENANT,
  SetCurrentTenantAction
} from '../tenant-state/tenant.actions';
import {
  getCurrentTenantSettings,
  getDefaultFeedOrigin,
  isPrivilegedUser,
  residentFeedOriginFor
} from '../tenant-state/tenant.selectors';
import { setCurrentUser } from '../user-state/user.actions';
import { SetCurrentEventsFiltersAction } from '../events-state/events.actions';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, Store } from '@ngrx/store';
import { from, of, switchMap } from 'rxjs';
import { filter, first, map, mergeMap } from 'rxjs/operators';
import {
  LOAD_CURRENT_NEWSFEED_FILTERS,
  RESET_CURRENT_NEWSFEED_FILTERS,
  SET_CURRENT_NEWSFEED_FILTERS,
  SetCurrentNewsfeedFiltersAction
} from './newsfeed.actions';
import { getCurrentNewsfeedFilters } from './newsfeed.selectors';

@Injectable()
export class NewsfeedEffects {
  initializeEventsAfterTenant$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SET_CURRENT_TENANT),
      concatLatestFrom(() => this.store.select(getCurrentNewsfeedFilters)),
      map(
        ([{ payload }, currentFilters]: [
          SetCurrentTenantAction,
          NewsfeedFilters
        ]) => ({
          type: SET_CURRENT_NEWSFEED_FILTERS,
          payload: {
            ...INITIAL_NEWSFEED_FILTERS,
            origin: payload?.tenant_setting?.filter_by_location_by_default
              ? 'my_settlement'
              : 'all',
            ...currentFilters
          }
        })
      )
    )
  );

  resetNewsfeedFilters$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RESET_CURRENT_NEWSFEED_FILTERS),
      concatLatestFrom(() => this.store.select(getDefaultFeedOrigin)),
      map(([, defaultOrigin]) => ({
        type: SET_CURRENT_NEWSFEED_FILTERS,
        payload: {
          ...INITIAL_NEWSFEED_FILTERS,
          origin: defaultOrigin
        }
      }))
    )
  );

  migrateResidentDefaultOrigin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(setCurrentUser),
      filter(user => !isPrivilegedUser(user)),
      mergeMap(() =>
        from(
          Preferences.get({
            key: STORAGE_KEYS.RESIDENT_ORIGIN_DEFAULT_MIGRATED
          })
        )
      ),
      filter(({ value }) => value !== 'true'),
      mergeMap(() => this.residentDefaultOrigin()),
      mergeMap(origin =>
        from(
          this.markDoneAndReadSavedFilters(
            STORAGE_KEYS.RESIDENT_ORIGIN_DEFAULT_MIGRATED
          )
        ).pipe(
          mergeMap(([newsfeed, events]) => {
            const actions: Action[] = [];
            if (newsfeed?.origin !== origin) {
              actions.push(
                new SetCurrentNewsfeedFiltersAction({
                  ...INITIAL_NEWSFEED_FILTERS,
                  ...newsfeed,
                  origin
                })
              );
            }
            if (events?.origin !== origin) {
              actions.push(
                new SetCurrentEventsFiltersAction({
                  ...INITIAL_EVENT_FILTERS,
                  ...events,
                  origin
                })
              );
            }
            return actions;
          })
        )
      )
    )
  );

  openResidentsOnAllPosts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(setCurrentUser),
      filter(user => !isPrivilegedUser(user)),
      mergeMap(() =>
        from(
          Preferences.get({
            key: STORAGE_KEYS.RESIDENT_ORIGIN_ALL_POSTS_APPLIED
          })
        )
      ),
      filter(({ value }) => value !== 'true'),
      mergeMap(() => this.residentDefaultOrigin()),
      filter(origin => origin === 'all'),
      mergeMap(() =>
        from(
          this.markDoneAndReadSavedFilters(
            STORAGE_KEYS.RESIDENT_ORIGIN_ALL_POSTS_APPLIED
          )
        ).pipe(
          mergeMap(([newsfeed, events]) => {
            const actions: Action[] = [];
            if (
              newsfeed?.origin === 'my_settlement' &&
              !newsfeed.filterByGroup?.length
            ) {
              actions.push(
                new SetCurrentNewsfeedFiltersAction({
                  ...INITIAL_NEWSFEED_FILTERS,
                  ...newsfeed,
                  origin: 'all'
                })
              );
            }
            if (events?.origin === 'my_settlement') {
              actions.push(
                new SetCurrentEventsFiltersAction({
                  ...INITIAL_EVENT_FILTERS,
                  ...events,
                  origin: 'all'
                })
              );
            }
            return actions;
          })
        )
      )
    )
  );

  loadCurrentNewsfeedFilters$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LOAD_CURRENT_NEWSFEED_FILTERS),
      mergeMap(() =>
        from(
          Preferences.get({ key: STORAGE_KEYS.CURRENT_NEWSFEED_FILTERS })
        ).pipe(
          map(v => JSON.parse(v.value)),
          map(t => ({
            type: SET_CURRENT_NEWSFEED_FILTERS,
            // Backfill any fields missing from persisted state so newly added
            // filters don't read as `undefined` and falsely activate the chip.
            payload: t
              ? { ...INITIAL_NEWSFEED_FILTERS, ...t }
              : INITIAL_NEWSFEED_FILTERS
          }))
        )
      )
    )
  );

  saveCurrentNewsfeedFiltersToStorage$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SET_CURRENT_NEWSFEED_FILTERS),
        concatLatestFrom(() => this.store.select(getCurrentNewsfeedFilters)),
        switchMap(([, f]) => {
          if (f !== null) {
            return from(
              Preferences.set({
                key: STORAGE_KEYS.CURRENT_NEWSFEED_FILTERS,
                value: JSON.stringify(f)
              })
            );
          } else {
            return of(f);
          }
        })
      ),
    { dispatch: false }
  );

  constructor(
    private actions$: Actions,
    private store: Store
  ) {}

  private residentDefaultOrigin() {
    return this.store
      .select(getCurrentTenantSettings)
      .pipe(filter(Boolean), first(), map(residentFeedOriginFor));
  }

  private async markDoneAndReadSavedFilters(
    doneKey: STORAGE_KEYS
  ): Promise<[NewsfeedFilters | null, ArticleEventFilter | null]> {
    const [newsfeed, events] = await Promise.all([
      Preferences.get({ key: STORAGE_KEYS.CURRENT_NEWSFEED_FILTERS }),
      Preferences.get({ key: STORAGE_KEYS.CURRENT_EVENTS_FILTERS })
    ]);
    await Preferences.set({ key: doneKey, value: 'true' });
    return [
      parseFilters<NewsfeedFilters>(newsfeed.value),
      parseFilters<ArticleEventFilter>(events.value)
    ];
  }
}

const parseFilters = <T>(raw: string | null): T | null =>
  raw ? (JSON.parse(raw) as T) : null;
